import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { useMemo, useState } from 'react';
import { api } from 'src/api';
import { BitcoinSingleTransactionResponse } from 'src/api/blockchain';
import { TxContext } from '../../Address/Bitcoin/TxHistoryTable/TxContext';
import { TxRecord } from '../../Address/Bitcoin/TxHistoryTable/TransactionRecord/TxRecord';
import { useAllAttributions } from 'src/hooks/attributions/useAllAttributions';
import { BsIncognito } from 'react-icons/bs';
import { TransactionSummary } from './TxHashDetails';
import '../../Address/Bitcoin/TxHistoryTable/TransactionRecord/TransactionInputsOutputsTable/styles.css';
import { TBlockchain } from 'src/types';

export const TxHash = () => {
  const { id: hash, chain } = useParams<{ id: string; chain: TBlockchain }>();
  const [showAttribution, setShowAttribution] = useState(true);

  const { data, isLoading, error } =
    useQuery<BitcoinSingleTransactionResponse | null>({
      queryKey: ['transaction', hash],
      queryFn: () => api.blockchain.getTransactionByHash(hash),
      enabled: !!hash,
    });

  // Extract transaction data for stable reference
  const transaction = data?.data;

  const transactions = useMemo(() => {
    return transaction ? [transaction] : [];
  }, [transaction]);

  useAllAttributions(chain as TBlockchain, transactions);

  if (isLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600"></div>
          <div className="text-gray-600">Loading transaction...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4">
          <div className="font-medium text-red-700">
            Error loading transaction
          </div>
          <div className="mt-1 text-sm text-red-600">
            {error instanceof Error
              ? error.message
              : 'Failed to load transaction data'}
          </div>
        </div>
      </div>
    );
  }

  if (!data || !transaction) {
    return (
      <div className="mx-auto max-w-4xl p-6">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="font-medium text-yellow-700">
            Transaction not found
          </div>
          <div className="mt-1 text-sm text-yellow-600">
            The transaction with hash &quot;{hash}&quot; could not be found.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl p-6">
      {/* Transaction Summary */}
      <div className="mb-6">
        <TransactionSummary transaction={transaction} />
      </div>

      {/* Note: netFlow and direction are intentionally omitted for standalone transactions
          as they only make sense when viewing transactions from a specific address context */}
      <div
        className={showAttribution ? 'show-attribution' : 'hide-attribution'}
      >
        <TxContext.Provider value={{ transaction }}>
          <TxRecord />
        </TxContext.Provider>
      </div>

      {/* Attribution Toggle Button */}
      <div className="fixed right-8 top-1/2 z-50 -translate-y-1/2 transform">
        <button
          onClick={() => setShowAttribution((prev) => !prev)}
          className="rounded-lg bg-white p-3 shadow-lg hover:bg-gray-100"
          title="Toggle attribution visibility"
        >
          <BsIncognito
            className={showAttribution ? 'text-black' : 'text-red-500'}
            size={20}
          />
        </button>
      </div>
    </div>
  );
};
